<template>
  <div v-if="filters && filters.length" class="cfg-filter-bar" :class="{ collapsed }">
    <div class="cfg-filter-bar-header" @click="collapsed = !collapsed">
      <div class="cfg-filter-bar-title">
        <i class="fa fa-filter" aria-hidden="true"></i>
        <span>Filters</span>
        <span v-if="activeCount" class="cfg-filter-badge">{{ activeCount }}</span>
      </div>
      <span class="cfg-filter-chevron">▼</span>
    </div>

    <div class="cfg-filter-bar-body" @click.stop>
      <div
        v-for="(f, i) in filters"
        :key="(f.column || 'col') + '-' + i"
        class="cfg-filter-group"
        :class="[
          { 'cfg-filter-group--sel': editMode && selectedFilterIndex === i },
          cfgReorderWrapClass('filter', i),
        ]"
        data-cfg-reorder="filter"
        :data-cfg-reorder-index="i"
      >
        <button
          v-if="editMode"
          type="button"
          class="cfg-drag-handle"
          title="Drag to reorder filter"
          aria-label="Drag to reorder filter"
          @mousedown.prevent="onCfgDragStart('filter', i, $event)"
        >⋮⋮</button>

        <div class="cfg-filter-inner">
        <div
          v-if="editMode"
          class="cfg-filter-hdr"
          :title="'Select filter: ' + (f.label || f.column)"
          @click="$emit('select-filter', i)"
        >
          <span class="cfg-filter-hdr-lbl">{{ f.label || f.column }}</span>
        </div>
        <div v-else class="cfg-filter-lbl-st">{{ f.label || f.column }}</div>

        <!-- Date range (vue2-datepicker — consistent across browsers) -->
        <template v-if="f.type === 'date_range'">
          <date-picker
            class="cfg-filter-date"
            :value="dateRangeValue(f)"
            type="date"
            range
            value-type="format"
            format="YYYY-MM-DD"
            :editable="false"
            :clearable="true"
            :confirm="false"
            placeholder="Select date range"
            popup-class="cfg-filter-date-popup"
            @input="onDateRangeInput(f, $event)"
            @clear="onDateRangeClear(f)"
          />
        </template>

        <!-- Single select -->
        <template v-else-if="f.type === 'single_select'">
          <select
            :value="values[f.column] || ''"
            @change="setSingle(f, $event.target.value)"
          >
            <option value="">All</option>
            <option v-for="v in optionFor(f)" :key="v" :value="v">{{ v }}</option>
          </select>
        </template>

        <!-- Multi select (default) -->
        <template v-else>
          <div
            class="cfg-ms-wrap"
            :class="{ 'cfg-ms-wrap--open': openCol === f.column }"
            :data-col="f.column"
          >
            <button
              type="button"
              class="cfg-ms-btn"
              :class="{ open: openCol === f.column }"
              @click.stop="toggle(f.column)"
            >
              <span class="cfg-ms-btn-label">{{ multiLabel(f) }}</span>
              <span class="cfg-ms-arrow">▾</span>
            </button>
            <div v-if="openCol === f.column" class="cfg-ms-dropdown">
              <label v-for="v in optionFor(f)" :key="v" class="cfg-ms-item">
                <input
                  type="checkbox"
                  :checked="isMultiChecked(f, v)"
                  @change="toggleMulti(f, v, $event.target.checked)"
                />
                <span>{{ v }}</span>
              </label>
            </div>
          </div>
        </template>
        </div>
      </div>

      <div class="cfg-filter-group cfg-filter-actions">
        <div class="cfg-filter-lbl-st" aria-hidden="true">&nbsp;</div>
        <div class="cfg-actions-row">
          <button class="cfg-btn cfg-btn-primary" @click="apply">Apply</button>
          <button class="cfg-btn cfg-btn-secondary" @click="reset">Reset</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

import { uniqueValues } from './utils/aggregations'
import cfgDragReorder from './mixins/cfgDragReorder'

export default {
  name: 'DashboardFilters',
  mixins: [cfgDragReorder],
  components: { DatePicker },
  props: {
    filters: { type: Array, default: () => [] },
    data: { type: Array, default: () => [] },
    value: { type: Object, default: () => ({}) },
    /** When true, filter label row becomes clickable to open the filter in the config sidebar. */
    editMode: { type: Boolean, default: false },
    /** Index of the filter selected in the sidebar, or -1 for none. */
    selectedFilterIndex: { type: Number, default: -1 },
  },
  data() {
    return {
      collapsed: false,
      openCol: null,
      values: this.cloneValue(this.value),
    }
  },
  computed: {
    activeCount() {
      let n = 0
      for (const f of this.filters) {
        const v = this.values[f.column]
        if (!v) continue
        if (f.type === 'date_range') { if (v.from || v.to) n++ }
        else if (f.type === 'single_select') { if (v) n++ }
        else if (Array.isArray(v) && v.length) n++
      }
      return n
    },
    /** Cached per column — avoids scanning full data on every render (critical for large sets). */
    filterOptionsByColumn() {
      const map = Object.create(null)
      for (const f of this.filters) {
        const col = f.column
        if (!col) continue
        if (Array.isArray(f.options) && f.options.length) {
          map[col] = f.options
        } else {
          map[col] = uniqueValues(this.data, col).sort((a, b) => String(a).localeCompare(String(b)))
        }
      }
      return map
    },
  },
  watch: {
    value: {
      deep: true,
      handler(v) { this.values = this.cloneValue(v) },
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClick)
  },
  methods: {
    cloneValue(v) {
      return JSON.parse(JSON.stringify(v || {}))
    },
    onDocClick(e) {
      if (!e.target.closest('.cfg-ms-wrap')) this.openCol = null
    },
    optionFor(f) {
      const col = f && f.column
      if (!col) return []
      return this.filterOptionsByColumn[col] || []
    },
    toggle(col) {
      this.openCol = this.openCol === col ? null : col
    },
    dateRangeValue(f) {
      const v = this.values[f.column]
      if (!v) return null
      const from = (v.from || '').trim()
      const to = (v.to || '').trim()
      if (from && to) return [from, to]
      if (from) return [from, from]
      if (to) return [to, to]
      return null
    },
    onDateRangeInput(f, val) {
      if (!val || !Array.isArray(val) || !val.length) {
        this.onDateRangeClear(f)
        return
      }
      const from = val[0] ? String(val[0]) : ''
      const to = val[1] ? String(val[1]) : from
      this.$set(this.values, f.column, { from, to })
    },
    onDateRangeClear(f) {
      this.$set(this.values, f.column, { from: '', to: '' })
    },
    setSingle(f, val) {
      this.$set(this.values, f.column, val)
    },
    isMultiChecked(f, v) {
      const arr = this.values[f.column]
      return Array.isArray(arr) && arr.includes(v)
    },
    toggleMulti(f, v, checked) {
      const arr = Array.isArray(this.values[f.column]) ? [...this.values[f.column]] : []
      const idx = arr.indexOf(v)
      if (checked && idx === -1) arr.push(v)
      if (!checked && idx !== -1) arr.splice(idx, 1)
      this.$set(this.values, f.column, arr)
    },
    multiLabel(f) {
      const arr = this.values[f.column]
      if (!Array.isArray(arr) || arr.length === 0) return 'All'
      if (arr.length <= 2) return arr.join(', ')
      return arr.length + ' selected'
    },
    apply() {
      this.$emit('apply', this.cloneValue(this.values))
    },
    reset() {
      this.values = {}
      this.$emit('apply', {})
    },
    onCfgReorder(kind, from, to) {
      if (kind === 'filter') this.$emit('reorder', { from, to })
    },
  },
}
</script>

<style scoped>
.cfg-filter-bar {
  margin-bottom: 20px;
  background: var(--dash-surface);
  border-radius: 12px;
  border: 1px solid var(--dash-border);
  box-shadow: 0 8px 24px -4px var(--dash-shadow);
  /* not hidden: overflow:hidden was clipping the multi-select popover; rounded corners from children/header only */
  overflow: visible;
  position: relative;
  /* Sits before KPIs/charts in the DOM: lift so custom dropdown (not a native <select>) paints above them */
  z-index: 20;
  /* #app uses text-align: center; avoid label/control jumping from inherited alignment */
  text-align: left;
}
.cfg-filter-bar-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 20px; cursor: pointer; user-select: none;
  border-radius: 12px 12px 0 0;
  background: var(--dash-surface);
}
.cfg-filter-bar-header:hover { background: var(--dash-surface2); }
.cfg-filter-bar-title {
  font-size: .8rem; font-weight: 600; text-transform: uppercase;
  letter-spacing: .5px; color: var(--dash-text-muted);
  display: flex; align-items: center; gap: 10px;
}
.cfg-filter-badge {
  background: var(--dash-accent); color: #fff;
  font-size: .7rem; font-weight: 700;
  padding: 2px 8px; border-radius: 999px;
  text-transform: none; letter-spacing: 0;
}
.cfg-filter-chevron { font-size: .7rem; color: var(--dash-text-muted); }
.cfg-filter-bar.collapsed .cfg-filter-chevron { transform: rotate(-90deg); }
.cfg-filter-bar-body {
  display: flex; gap: 14px; flex-wrap: wrap; align-items: flex-end;
  padding: 0 20px 16px 20px;
  max-height: 700px; opacity: 1;
}
.cfg-filter-bar.collapsed .cfg-filter-bar-body {
  max-height: 0; opacity: 0; padding-top: 0; padding-bottom: 0; overflow: hidden;
}

.cfg-filter-group {
  display: flex; flex-direction: row; flex-wrap: nowrap; align-items: flex-start;
  gap: 8px; min-width: 0;
}
.cfg-filter-inner {
  display: flex; flex-direction: column; gap: 6px; min-width: 0; flex: 1;
}
.cfg-drag-handle {
  flex-shrink: 0;
  padding: 4px 6px;
  margin-top: 2px;
  border: 1px solid var(--dash-border);
  border-radius: 6px;
  background: var(--dash-surface2);
  color: var(--dash-text-muted);
  font-size: .75rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -1px;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;
}
.cfg-drag-handle:hover { color: var(--dash-accent); border-color: var(--dash-accent); }
.cfg-drag-handle:active { cursor: grabbing; }
.cfg-reorder-wrap--dragging { opacity: 0.45; }
.cfg-reorder-wrap--drop {
  box-shadow: 0 0 0 2px var(--dash-accent, #3b82f6);
  border-radius: 10px;
}
.cfg-filter-lbl-st {
  display: block;
  font-size: .72rem; color: var(--dash-text-muted); font-weight: 600;
  text-transform: uppercase; letter-spacing: .6px;
  line-height: 1.3;
  /* neutralize any global label / Bootstrap styles */
  position: static;
  margin: 0; padding: 0;
  transform: none;
  transition: none;
  animation: none;
}
/* Apply/Reset column: keep buttons on same baseline as other inputs */
.cfg-filter-actions {
  flex-direction: column;
  align-self: flex-end;
}
.cfg-filter-actions .cfg-drag-handle { display: none; }
.cfg-filter-hdr {
  display: flex; align-items: center; justify-content: flex-start; gap: 8px;
  font-size: .72rem; color: var(--dash-text-muted); font-weight: 600;
  text-transform: uppercase; letter-spacing: .6px;
  padding: 2px 0; cursor: pointer; border-radius: 6px; margin: -2px -4px; padding-left: 4px; padding-right: 4px;
  user-select: none;
}
.cfg-filter-hdr:hover { background: var(--dash-surface2); color: var(--dash-text); }
.cfg-filter-hdr-lbl { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cfg-filter-group--sel {
  box-shadow: 0 0 0 2px var(--dash-accent, #3b82f6);
  border-radius: 10px; padding: 6px; margin: -6px; margin-bottom: 8px; align-self: flex-start;
}
.cfg-filter-group--sel .cfg-filter-hdr { color: var(--dash-accent, #3b82f6); }
.cfg-filter-group select {
  padding: 8px 12px; border-radius: 8px; border: 1px solid var(--dash-input-border);
  background: var(--dash-input-bg); color: var(--dash-text); font-size: .88rem;
  min-width: 140px; height: 36px; outline: none;
  box-sizing: border-box;
  box-shadow: 0 0 0 3px transparent;
  transition: none; animation: none;
}
.cfg-filter-group select:focus {
  border-color: var(--dash-accent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--dash-accent) 18%, transparent);
}

/* vue2-datepicker — match filter bar theme */
.cfg-filter-date {
  min-width: 220px;
  width: 100%;
}
.cfg-filter-date ::v-deep .mx-datepicker {
  width: 100%;
}
.cfg-filter-date ::v-deep .mx-input-wrapper {
  width: 100%;
}
.cfg-filter-date ::v-deep .mx-input {
  height: 36px;
  padding: 8px 32px 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--dash-input-border);
  background: var(--dash-input-bg);
  color: var(--dash-text);
  font-size: .88rem;
  box-shadow: 0 0 0 3px transparent;
  box-sizing: border-box;
}
.cfg-filter-date ::v-deep .mx-input:focus,
.cfg-filter-date ::v-deep .mx-input:hover {
  border-color: var(--dash-accent);
}
.cfg-filter-date ::v-deep .mx-input:focus {
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--dash-accent) 18%, transparent);
}
.cfg-filter-date ::v-deep .mx-icon-calendar,
.cfg-filter-date ::v-deep .mx-icon-clear {
  color: var(--dash-text-muted);
}
.cfg-filter-date ::v-deep .mx-icon-calendar:hover,
.cfg-filter-date ::v-deep .mx-icon-clear:hover {
  color: var(--dash-accent);
}

.cfg-ms-wrap { position: relative; min-width: 160px; }
.cfg-ms-wrap--open { z-index: 50; }
.cfg-ms-btn {
  width: 100%;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 8px 12px; border-radius: 8px;
  border: 1px solid var(--dash-input-border); background: var(--dash-input-bg);
  color: var(--dash-text); font-size: .88rem; cursor: pointer; height: 36px;
}
.cfg-ms-btn:hover { border-color: var(--dash-accent); }
.cfg-ms-btn-label {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: left; flex: 1;
}
.cfg-ms-arrow { color: var(--dash-text-muted); font-size: .75rem; }
.cfg-ms-dropdown {
  position: absolute; top: calc(100% + 4px); left: 0; min-width: 100%;
  background: var(--dash-surface); border: 1px solid var(--dash-border); border-radius: 8px;
  box-shadow: 0 10px 28px -6px var(--dash-shadow);
  z-index: 1000;
  max-height: 240px; overflow-y: auto; padding: 4px 0;
}
.cfg-ms-item {
  display: flex; align-items: center; gap: 8px; padding: 6px 10px;
  font-size: .85rem; color: var(--dash-text); cursor: pointer;
  white-space: nowrap;
}
.cfg-ms-item:hover { background: var(--dash-surface2); }
.cfg-ms-item input[type="checkbox"] {
  width: 14px; height: 14px; accent-color: var(--dash-accent);
}

.cfg-actions-row { display: flex; gap: 8px; }
.cfg-btn {
  height: 36px; padding: 0 16px; border-radius: 8px; border: 1px solid transparent;
  font-size: .85rem; font-weight: 600; cursor: pointer;
}
.cfg-btn-primary { background: var(--dash-accent); color: #fff; }
.cfg-btn-primary:hover { filter: brightness(1.05); box-shadow: 0 4px 12px -2px color-mix(in srgb, var(--dash-accent) 50%, transparent); }
.cfg-btn-secondary {
  background: var(--dash-surface2); color: var(--dash-text); border-color: var(--dash-border);
}
.cfg-btn-secondary:hover { background: var(--dash-border); }

@media (max-width: 768px) {
  .cfg-filter-group { flex: 1 1 calc(50% - 8px); }
  .cfg-filter-group select,
  .cfg-ms-btn,
  .cfg-filter-date { min-width: 0; width: 100%; }
  .cfg-ms-wrap { min-width: 0; width: 100%; }
}
@media (max-width: 480px) {
  .cfg-filter-group { flex: 1 1 100%; }
  .cfg-actions-row { width: 100%; }
  .cfg-actions-row .cfg-btn { flex: 1; }
}
</style>

<style>
/* vue2-datepicker popup (rendered on document.body) */
.cfg-filter-date-popup.mx-datepicker-popup {
  z-index: 1100;
  border-radius: 10px;
  border: 1px solid var(--dash-border, #e2e8f0);
  box-shadow: 0 10px 28px -6px rgba(0, 0, 0, 0.15);
  background: var(--dash-surface, #fff);
  color: var(--dash-text, #1e293b);
}
.cfg-filter-date-popup .mx-calendar-content .cell.active,
.cfg-filter-date-popup .mx-calendar-content .cell.in-range,
.cfg-filter-date-popup .mx-calendar-content .cell.hover-in-range {
  background: color-mix(in srgb, var(--dash-accent, #3b82f6) 18%, transparent);
  color: var(--dash-accent, #3b82f6);
}
.cfg-filter-date-popup .mx-calendar-content .cell:hover {
  background: var(--dash-surface2, #f1f5f9);
}
.cfg-filter-date-popup .mx-btn-text,
.cfg-filter-date-popup .mx-table-date .today {
  color: var(--dash-accent, #3b82f6);
}
</style>
